import {
  useDeleteConversionRateMutation,
} from "api/ruleEngineApi";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";

interface DeleteConversionRateModalProps {
  show: boolean;
  fromCurrency: string | undefined;
  toCurrency: string | undefined;
  rateId: string | undefined;
  onClose?: () => void;
  onSuccess?: () => void;
  onDelete: () => void;
  selectedRule?: any;
}

const DeleteConversionRateModal = ({
  show,
  onClose,
  onSuccess,
  selectedRule,
  fromCurrency,
  toCurrency,
  rateId,
  onDelete,
}: DeleteConversionRateModalProps) => {
  const [deleteConversionRate, { isLoading: isDeleting }] =
    useDeleteConversionRateMutation();

  const handleDelete = async () => {
    if (!rateId) {
      showToastError("No rule selected for deletion");
      return;
    }

    try {
      await deleteConversionRate(rateId).unwrap();
      showToastSuccess("Conversion rate deleted successfully");
      onDelete();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error deleting conversion rate:", error);
      showToastError(error?.data?.message || "Failed to delete conversion rate");
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onClose?.();
    }
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "40px",
          maxWidth: "500px",
          width: "90%",
          position: "relative",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
          animation: show ? "modalFadeIn 0.2s ease-out" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isDeleting}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            backgroundColor: "transparent",
            border: "none",
            fontSize: "24px",
            color: "#666",
            cursor: isDeleting ? "not-allowed" : "pointer",
            padding: "5px",
            lineHeight: 1,
            opacity: isDeleting ? 0.5 : 1,
          }}
        >
          ✕
        </button>

        {/* Modal Content */}
        <div>
          <h5
            style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#000",
              marginBottom: "32px",
              marginTop: 0,
              lineHeight: 1.2,
            }}
          >
            Delete Conversion Rate
          </h5>

          <p
            style={{
              fontSize: "18px",
              color: "#333",
              marginBottom: "48px",
              lineHeight: 1.4,
            }}
          >
            Are you sure you want to delete{" "}
            <strong>
              {fromCurrency} - {toCurrency}
            </strong>
            ?
          </p>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "flex-start",
            }}
          >
            <button
              onClick={onClose}
              disabled={isDeleting}
              style={{
                backgroundColor: "#007AFF",
                color: "white",
                border: "none",
                borderRadius: "25px",
                padding: "14px 32px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: isDeleting ? "not-allowed" : "pointer",
                minWidth: "120px",
                opacity: isDeleting ? 0.6 : 1,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = "#0056CC";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = "#007AFF";
                }
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleDelete}
              disabled={isDeleting}
              style={{
                backgroundColor: "#DC3545",
                color: "white",
                border: "none",
                borderRadius: "25px",
                padding: "14px 32px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: isDeleting ? "not-allowed" : "pointer",
                minWidth: "120px",
                opacity: isDeleting ? 0.6 : 1,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = "#B02A37";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.backgroundColor = "#DC3545";
                }
              }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};

export default DeleteConversionRateModal;
